package com.banyan.flightviewer.web.components.aircraft

import com.banyan.flightviewer.state.AircraftData

/**
 * Aircraft-list filter state owned by [com.banyan.flightviewer.web.state.WebUiState].
 * Ported from desktop/src/main/kotlin/com/banyan/flightviewer/ui/FilterPanel.kt.
 *
 * A filter is considered "active" when any field differs from the default.
 * The TopBar highlights the filter icon in that case so it's clear something
 * is hiding aircraft.
 */
data class AircraftFilters(
    val altMin: Int = 0,
    val altMax: Int = 60_000,
    val showGround: Boolean = true,
    val showAirborne: Boolean = true,
    val airlinePrefix: String = "",
) {
    val isActive: Boolean
        get() = altMin > 0 ||
            altMax < 60_000 ||
            !showGround ||
            !showAirborne ||
            airlinePrefix.isNotBlank()
}

/**
 * Single filter pipeline used by both the map and the table.
 *
 * Mirrors [App.kt:335-372](desktop/src/main/kotlin/com/banyan/flightviewer/ui/App.kt):
 *  - search matches any of callsign / origin / dest / type / registration / hex (case-insensitive contains)
 *  - altitude range is inclusive; `isGround` aircraft bypass the range check but still
 *    have to pass the ground/airborne toggle
 *  - airline prefix matches the callsign's leading characters, uppercased on both sides
 */
fun filterAircraft(
    list: List<AircraftData>,
    query: String,
    filters: AircraftFilters,
): List<AircraftData> {
    var out = list

    if (query.isNotBlank()) {
        val q = query.lowercase()
        out = out.filter { ac ->
            ac.callsign.lowercase().contains(q) ||
                ac.origin.lowercase().contains(q) ||
                ac.destination.lowercase().contains(q) ||
                ac.aircraftType.lowercase().contains(q) ||
                ac.registration.lowercase().contains(q) ||
                ac.hex.lowercase().contains(q)
        }
    }

    if (filters.isActive) {
        val prefix = filters.airlinePrefix.uppercase()
        out = out.filter { ac ->
            val alt = ac.altitudeNumeric
            val isGround = alt <= 0 || ac.altitude.equals("ground", ignoreCase = true)
            val altInRange = alt >= filters.altMin && alt <= filters.altMax
            val groundFilter = if (isGround) filters.showGround else filters.showAirborne
            val airlineFilter = prefix.isBlank() || ac.callsign.uppercase().startsWith(prefix)
            (altInRange || isGround) && groundFilter && airlineFilter
        }
    }

    return out
}
